// React/finance-app/src/features/tt-insights/useTTInsights.js
import { useEffect, useMemo, useState } from 'react';
import {
  getMonthlyLeaderboard,
  getAllUsersCurWeekRebates,
  getRebates,
  getSummary,
  getKpiState,
  getNotificationsHistory,
  getTopBuyersMonth,
  getAvailableBalances,
  getUserBalance,
  getNewModelMetrics,
} from './services';
import { fmtDate } from './utils';

export default function useTTInsights() {
  // Leaderboard (we keep the full arrays here)
  const [leaderboardCur, setLeaderboardCur] = useState([]);
  const [leaderboardPrev, setLeaderboardPrev] = useState([]);
  const [monthCur, setMonthCur] = useState('');
  const [monthPrev, setMonthPrev] = useState('');

  const [rebatesCur, setRebatesCur] = useState([]);
  const [rebatesPrev1, setRebatesPrev1] = useState([]);
  const [rebatesPrev2, setRebatesPrev2] = useState([]);

  const [userCode, setUserCode] = useState('');
  const [userRebates, setUserRebates] = useState([]);
  const [pendingCount, setPendingCount] = useState(null);
  const [rebateTotals, setRebateTotals] = useState(null);

  const [summary, setSummary] = useState(null);
  const [kpiState, setKpiState] = useState(null);

  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return fmtDate(d);
  });
  const [dateTo, setDateTo] = useState(() => fmtDate(new Date()));

  const [notifications, setNotifications] = useState([]);
  const [notificationLimit, setNotificationLimit] = useState(10);

  // Top buyers (new response)
  const [topCurUsers, setTopCurUsers] = useState([]);
  const [topPrevUsers, setTopPrevUsers] = useState([]);
  const [topCurPeriod, setTopCurPeriod] = useState('');
  const [topPrevPeriod, setTopPrevPeriod] = useState('');
  const [topMeta, setTopMeta] = useState(null);
  const [topLimit, setTopLimit] = useState(50);

  // Balances
  const [balances, setBalances] = useState([]);
  const [balancesMeta, setBalancesMeta] = useState(null);
  const [balancesLimit, setBalancesLimit] = useState(200);

  const [userBalance, setUserBalance] = useState(null);

  const [newModelMetrics, setNewModelMetrics] = useState(null);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState({
    board: false,
    weeks: false,
    rebates: false,
    notes: false,
    summary: false,
    kpi: false,
    topBuyers: false,
    balances: false,
    userBalance: false,
    newModelMetrics: false,
  });

  // ----------------------------
  // Fetchers
  // ----------------------------
  const fetchLeaderboard = async () => {
    try {
      setError(null);
      setLoading((s) => ({ ...s, board: true }));
      const res = await getMonthlyLeaderboard();
      const data = res?.data || {};

      // Be resilient to either shape:
      // A) { current: [...], previous: [...], month_cur: 'YYYY-MM', month_prev: 'YYYY-MM' }
      // B) { current_month: { month: 'YYYY-MM', rankings: [...] }, previous_month: { month, rankings } }
      const cur =
        Array.isArray(data.current)
          ? data.current
          : Array.isArray(data?.current_month?.rankings)
            ? data.current_month.rankings
            : Array.isArray(data?.current_month)
              ? data.current_month
              : [];

      const prev =
        Array.isArray(data.previous)
          ? data.previous
          : Array.isArray(data?.previous_month?.rankings)
            ? data.previous_month.rankings
            : Array.isArray(data?.previous_month)
              ? data.previous_month
              : [];

      const mCur = data.month_cur ?? data?.current_month?.month ?? '';
      const mPrev = data.month_prev ?? data?.previous_month?.month ?? '';

      setLeaderboardCur(cur);
      setLeaderboardPrev(prev);
      setMonthCur(mCur);
      setMonthPrev(mPrev);
    } catch (err) {
      console.error('Failed to fetch leaderboard', err);
      setError(err?.response?.data?.detail || 'Failed to load leaderboard');
    } finally {
      setLoading((s) => ({ ...s, board: false }));
    }
  };

  const fetchWeeklyRebates = async () => {
    try {
      setError(null);
      setLoading((s) => ({ ...s, weeks: true }));
      const res = await getAllUsersCurWeekRebates();
      setRebatesCur(res.data?.current_week_rebates ?? []);
      setRebatesPrev1(res.data?.previous_week_rebates ?? []);
      setRebatesPrev2(res.data?.week_before_previous_rebates ?? []);
    } catch (err) {
      console.error('Failed to fetch weekly rebates', err);
      setError(err.response?.data?.detail || 'Failed to load weekly rebates');
    } finally {
      setLoading((s) => ({ ...s, weeks: false }));
    }
  };

  const fetchUserRebates = async () => {
    const code = userCode.trim();
    if (!code) return;
    try {
      setError(null);
      setLoading((s) => ({ ...s, rebates: true }));
      const res = await getRebates({ user_code: code, date_from: dateFrom, date_to: dateTo });
      setUserRebates(res.data?.data ?? []);
      setRebateTotals(res.data?.totals ?? null);
      setPendingCount(res.data?.pending_count ?? null);
    } catch (err) {
      console.error('Failed to fetch user rebates', err);
      setError(err.response?.data?.detail || 'Failed to load user rebates');
      setUserRebates([]);
      setRebateTotals(null);
      setPendingCount(null);
    } finally {
      setLoading((s) => ({ ...s, rebates: false }));
    }
  };

  const fetchSummary = async () => {
    const code = userCode.trim();
    if (!code) return;
    try {
      setError(null);
      setLoading((s) => ({ ...s, summary: true }));
      const res = await getSummary(code);
      setSummary(res.data?.data ?? {});
    } catch (err) {
      console.error('Failed to fetch summary', err);
      setError(err.response?.data?.detail || 'Failed to load summary');
      setSummary(null);
    } finally {
      setLoading((s) => ({ ...s, summary: false }));
    }
  };

  const fetchKpiState = async () => {
    const code = userCode.trim();
    if (!code) return;
    try {
      setError(null);
      setLoading((s) => ({ ...s, kpi: true }));
      const res = await getKpiState(code);
      setKpiState(res.data?.data ?? {});
    } catch (err) {
      console.error('Failed to fetch KPI state', err);
      setError(err.response?.data?.detail || 'Failed to load KPI state');
      setKpiState(null);
    } finally {
      setLoading((s) => ({ ...s, kpi: false }));
    }
  };

  const fetchUserNotifications = async () => {
    const code = userCode.trim();
    if (!code) return;
    try {
      setError(null);
      setLoading((s) => ({ ...s, notes: true }));
      const res = await getNotificationsHistory(code, notificationLimit);
      setNotifications(res.data?.data ?? []);
    } catch (err) {
      console.error('Failed to fetch user notifications', err);
      setError(err.response?.data?.detail || 'Failed to load notifications');
    } finally {
      setLoading((s) => ({ ...s, notes: false }));
    }
  };

  const fetchTopBuyers = async () => {
    try {
      setError(null);
      setLoading((s) => ({ ...s, topBuyers: true }));
      const res = await getTopBuyersMonth();

      const block = res.data?.data || {};
      const cm = block.current_month || {};
      const pm = block.previous_month || {};

      setTopCurUsers(cm.users || []);
      setTopPrevUsers(pm.users || []);
      setTopCurPeriod(cm.period || cm.title || '');
      setTopPrevPeriod(pm.period || pm.title || '');
      setTopMeta(res.data?.meta || null);
    } catch (err) {
      console.error('Failed to fetch top buyers', err);
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          'Failed to fetch top buyers data'
      );
      setTopCurUsers([]);
      setTopPrevUsers([]);
      setTopCurPeriod('');
      setTopPrevPeriod('');
      setTopMeta(null);
    } finally {
      setLoading((s) => ({ ...s, topBuyers: false }));
    }
  };

  const fetchBalances = async () => {
    try {
      setError(null);
      setLoading((s) => ({ ...s, balances: true }));
      const res = await getAvailableBalances();
      setBalances(res.data?.data ?? []);
      setBalancesMeta(res.data?.meta ?? null);
    } catch (err) {
      console.error('Failed to fetch balances', err);
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          'Failed to fetch available balances'
      );
      setBalances([]);
      setBalancesMeta(null);
    } finally {
      setLoading((s) => ({ ...s, balances: false }));
    }
  };

  const fetchUserBalance = async (code) => {
    const c = (code ?? userCode).trim();
    if (!c) return;
    try {
      setError(null);
      setLoading((s) => ({ ...s, userBalance: true }));
      const res = await getUserBalance(c);
      setUserBalance(res.data?.data ?? null);
    } catch (err) {
      console.error('Failed to fetch user balance', err);
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          'Failed to fetch user balance'
      );
      setUserBalance(null);
    } finally {
      setLoading((s) => ({ ...s, userBalance: false }));
    }
  };

  const fetchNewModelMetrics = async () => {
    const code = userCode.trim();
    if (!code) return;
    try {
      setError(null);
      setLoading((s) => ({ ...s, newModelMetrics: true }));
      const res = await getNewModelMetrics(code);
      // Handle both wrapped (res.data.data) and direct (res.data) response formats
      const metrics = res.data?.data ?? res.data ?? null;
      console.log('New Model Metrics Response:', { 
        fullResponse: res, 
        resData: res.data, 
        metrics,
        roi: metrics?.roi,
        roiType: typeof metrics?.roi
      });
      // Ensure numeric values (in case they come as strings)
      if (metrics) {
        setNewModelMetrics({
          total_rebate: typeof metrics.total_rebate === 'number' ? metrics.total_rebate : parseFloat(metrics.total_rebate) || 0,
          roi: typeof metrics.roi === 'number' ? metrics.roi : parseFloat(metrics.roi) || 0,
          rank: typeof metrics.rank === 'number' ? metrics.rank : parseInt(metrics.rank) || null,
          new_model_withdrawals: typeof metrics.new_model_withdrawals === 'number' ? metrics.new_model_withdrawals : parseFloat(metrics.new_model_withdrawals) || 0,
        });
      } else {
        setNewModelMetrics(null);
      }
    } catch (err) {
      console.error('Failed to fetch new model metrics', err);
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          'Failed to fetch new model metrics'
      );
      setNewModelMetrics(null);
    } finally {
      setLoading((s) => ({ ...s, newModelMetrics: false }));
    }
  };

  useEffect(() => {
    fetchLeaderboard();
    fetchWeeklyRebates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------
  // Derived
  // ----------------------------
  const curAll = leaderboardCur;          // full list
  const prevAll = leaderboardPrev;        // full list

  const curTop20 = leaderboardCur.slice(0, 20);
  const prevTop20 = leaderboardPrev.slice(0, 20);

  const rebatesCurNZ = useMemo(
    () => (rebatesCur || []).filter((e) => Number(e?.rebate) !== 0),
    [rebatesCur]
  );
  const rebatesPrev1NZ = useMemo(
    () => (rebatesPrev1 || []).filter((e) => Number(e?.rebate) !== 0),
    [rebatesPrev1]
  );
  const rebatesPrev2NZ = useMemo(
    () => (rebatesPrev2 || []).filter((e) => Number(e?.rebate) !== 0),
    [rebatesPrev2]
  );

  const kpiRows = useMemo(() => {
    if (!kpiState || typeof kpiState !== 'object') return [];
    return Object.entries(kpiState).map(([name, obj]) => {
      const cur = obj?.current || {};
      const prev = obj?.previous || {};
      return {
        name,
        curValue: cur.value,
        curRank: cur.rank,
        curDate: cur.date,
        prevValue: prev?.value ?? null,
        prevRank: prev?.rank ?? null,
        prevDate: prev?.date ?? null,
      };
    });
  }, [kpiState]);

  return {
    // state
    leaderboardCur,
    leaderboardPrev,
    monthCur,
    monthPrev,
    rebatesCur,
    rebatesPrev1,
    rebatesPrev2,
    userCode,
    setUserCode,
    userRebates,
    pendingCount,
    rebateTotals,
    summary,
    kpiState,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    notifications,
    notificationLimit,
    setNotificationLimit,
    topCurUsers,
    topPrevUsers,
    topCurPeriod,
    topPrevPeriod,
    topMeta,
    topLimit,
    setTopLimit,
    balances,
    balancesMeta,
    balancesLimit,
    setBalancesLimit,
    userBalance,
    newModelMetrics,
    error,
    setError,
    loading,

    // derived (include full lists so the tab can “Show more”)
    curAll,
    prevAll,
    curTop20,
    prevTop20,
    rebatesCurNZ,
    rebatesPrev1NZ,
    rebatesPrev2NZ,
    kpiRows,

    // actions
    fetchLeaderboard,
    fetchWeeklyRebates,
    fetchUserRebates,
    fetchSummary,
    fetchKpiState,
    fetchUserNotifications,
    fetchTopBuyers,
    fetchBalances,
    fetchUserBalance,
    fetchNewModelMetrics,
  };
}
